import { Skeleton } from "@mui/material";
import { isLoading } from "apollo/utils";
import clsx from "clsx";
import { usePatientFilesContext } from "containers/PatientFilesProvider";
import {
  AHB_RESPONSE_STATUS,
  AHB_WITHDRAW_REASON,
  FILE_SCOPE,
  FILE_TABLE_ACTION_DELETE,
  FILE_TABLE_ACTION_DOWNLOAD,
  REHAB_FORM_NAMES,
  TRACK_EVENTS,
} from "core/consts";
import {
  isAHBStatusDelivered,
  isAHBStatusRetrying,
  isAHBWithdrawalMessage,
} from "core/consts/typeGuards";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { MAX_FILE_SIZE_AHB_MB } from "core/model/files/config";
import { formatUnixDate } from "core/model/utils/dates";
import {
  activateDTAAttachments,
  activateFileDeleteModalRedesign,
} from "core/model/utils/featureFlags";
import { getKey } from "core/model/utils/strings";
import {
  AHBMessage,
  Auction,
  FileParams,
  FileState,
  Filev2,
  Patient,
  RehabFormName,
} from "core/types";
import { isValid } from "date-fns";
import { Banner, Button } from "ds/ui";
import ConfirmationDialog from "ds_legacy/components/ConfirmationDialog";
import InfoChip from "ds_legacy/components/InfoChip";
import RSButton from "ds_legacy/components/RSButton";
import { GeneralListAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { margin } from "ds_legacy/materials/metrics";
import { SkeletonRectangle } from "ds_legacy/materials/shapes";
import { ApprovalLinkCopyButton } from "dsl/atoms/CopyButton";
import { useGetFilesFeatures } from "dsl/ecosystems/PatientFiles/useGetFilesFeatures";
import {
  getFileNameAndIsNewVersionAvailable,
  getFileSize,
  isFileBeingDeleted,
} from "dsl/ecosystems/PatientFiles/utils";
import { useCareseekerHandlerUrls } from "dsl/hooks/useNavigationHandlers";
import useProductTour, {
  PRODUCT_TOURS,
  tourAttributes,
} from "dsl/molecules/useProductTour";
import { isFileHISStateful, isFileV2 } from "dsl/organisms/FileTables";
import FileDeleteModal from "dsl/organisms/Modals/FileDeleteModal";
import FileDeleteModalv2 from "dsl/organisms/Modals/FileDeleteModalv2";
import { UploadFilesModal } from "dsl/organisms/UploadFilesModal";
import {
  ExternalLinkIcon,
  FileOutputIcon,
  LinkIcon,
  LockIcon,
  LucideFolderInput,
  PrinterIcon,
  TrashIcon,
} from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useLocale, useTranslations } from "translations";
import { LinkToForm, StyledFormCard } from ".";
import { WithdrawMessageReasonModal } from "./WithdrawMessageReasonModal";
import {
  AHBMessageSectionProps,
  getRehabFormGoToLinkTrackEvent,
  useAHBMessageSection,
  useGetStatusChip,
  useGetTranslateWithdrawReason,
  useMissingIdentifiers,
  useTranslateAHBMessageType,
} from "./useAHBMessageSection";

function AsyncFileName({ file }: { file: FileState }) {
  const [fileName, setFileName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function resolveFileName() {
      try {
        const result = await getFileNameAndIsNewVersionAvailable({
          canAccessPatientData: true,
          file,
        });
        setFileName(String(result.value));
      } catch {
        setFileName("Unknown"); // #TODO: Add translation
      } finally {
        setIsLoading(false);
      }
    }
    resolveFileName();
  }, [file]);

  if (isLoading) {
    return <SkeletonRectangle height="16px" width="100px" />;
  }

  return <span>{fileName}</span>;
}

function AHBRequestHistoryItem({ message }: { message: AHBMessage }) {
  const locale = useLocale();
  const translations = useTranslations();
  const getTranslateMessageType = useTranslateAHBMessageType();
  const getTranslateWithdrawReason = useGetTranslateWithdrawReason();
  const isWithdrawalType = isAHBWithdrawalMessage(message);
  const isDeliveredStatus = isAHBStatusDelivered(message);

  return (
    <li
      key={getKey(message.message_id)}
      className={clsx("text-sm", {
        "font-semibold text-danger": isWithdrawalType,
        "text-gray-600": !isWithdrawalType,
      })}
    >
      <span className="pr-2">
        {isValid(message.created_at)
          ? formatUnixDate(message.created_at, locale, { withTime: true })
          : "-"}
      </span>

      <span
        className={clsx("", {
          "font-semibold text-danger": isWithdrawalType,
          "text-gray-900": !isWithdrawalType,
        })}
      >
        {getTranslateMessageType(message)}
      </span>

      {isWithdrawalType && isDeliveredStatus && (
        <>
          <br />
          <span>
            {translations.insuranceApp.eligibilityTab.sendDocuments.historyFormReason(
              {
                withdrawReason: getTranslateWithdrawReason(
                  message.withdraw_reason,
                ),
              },
            )}
            {message.withdraw_reason === AHB_WITHDRAW_REASON.OTHER
              ? ` - ${message.withdraw_reason_other}`
              : null}
          </span>
        </>
      )}
    </li>
  );
}

function EligibilityAction({
  auction,
  formName,
  isApprovedByDoctor,
  isDRVFormCreated,
  isGKVFormCreated,
  isGKVMedicalForm,
  patient,
  testId,
}: {
  auction: Auction;
  formName: RehabFormName;
  isApprovedByDoctor: boolean;
  isDRVFormCreated: boolean;
  isGKVFormCreated: boolean;
  isGKVMedicalForm: boolean;
  patient: Patient;
  testId: string;
}) {
  const translations = useTranslations();
  const { goToRehabFormApproval } = useCareseekerHandlerUrls();

  if (isDRVFormCreated) {
    return null;
  }

  if (!isGKVFormCreated) {
    return (
      <LinkToForm
        auction={auction}
        formName={formName}
        label={
          translations.insuranceApp.eligibilityTab.sendDocuments.toFormLink
        }
        patient={patient}
        testId={testId}
        trackEventProps={{
          name: getRehabFormGoToLinkTrackEvent(formName),
        }}
      />
    );
  }

  if (isGKVMedicalForm && !isApprovedByDoctor) {
    return (
      <ApprovalLinkCopyButton
        href={goToRehabFormApproval({
          auctionId: auction.id,
          careseekerId: patient.careseeker?.id ?? -1,
          patientId: patient.id,
        })}
        label={
          translations.insuranceApp.eligibilityTab.approvalFlow.buttonCopyLink
        }
      />
    );
  }

  return <span />;
}

export function AHBMessageSection({
  ahbMessagesPayload,
  auction,
  auctionForms,
  hasTwoWaySync,
  patient,
  queryProgress,
  refetchAHBMessageThreads,
}: AHBMessageSectionProps) {
  const translations = useTranslations();
  const locale = useLocale();

  const { isDocsAndExtractActive } = useIsDocsAndExtractActive();

  const params: FileParams = isDocsAndExtractActive
    ? `auction_id=${auction.id}`
    : `auction_id=${auction.id}&scope=${FILE_SCOPE.ELIGIBILITY}`;

  const {
    action,
    handleDeleteFile,
    handleReset,
    handleUploadFile,
    handleUploadFilesHIS,
    mutationProgress,
    setAction,
  } = usePatientFilesContext({
    externalId: patient.external_id,
    params,
  });

  const {
    availableFiles,
    getFilesHISProgress,
    unavailableFiles,
    uploadedFilesV2,
  } = useGetFilesFeatures({
    action,
    externalId: patient.external_id,
    isEligiblePage: true,
    params,
  });

  const filteredUploadedFilesV2 = uploadedFilesV2?.filter(
    (file) => isFileV2(file) && file.file_scope === FILE_SCOPE.ELIGIBILITY,
  );

  const {
    ahbMessageThreads,
    canWithdrawMessage,
    confirmSendRequestProps,
    fetchXMLProgress,
    handleCopyXML,
    handlePrintDocument,
    handleSendAHBRequest,
    handleSendToHis,
    handleViewDocument,
    handleWithdrawAHBRequest,
    isDRVFormCreated,
    isFileDeleteDisabled,
    isFileSizeLimitExceeded,
    isFileUploadDisabled,
    isGKVGeneralFormCreated,
    isGKVMedicalFormCreated,
    isMedicalformApprovedByDoctor,
    isSendButtonDisabled,
    mostRecentThread,
    progress,
    progress2,
    responses,
    sendProgress,
    sendToHisProgress,
    shouldShowSendButton,
    totalFileSizeMB,
    withdrawDialogProps,
    withdrawProgress,
  } = useAHBMessageSection({
    ahbMessagesPayload,
    auction,
    auctionForms,
    files: filteredUploadedFilesV2 ?? undefined,
    patient,
    refetchAHBMessageThreads,
  });

  const missingIdentifiersMessage = useMissingIdentifiers({ patient });
  const getStatusChip = useGetStatusChip();

  useProductTour({
    skip:
      !!mostRecentThread || isGKVGeneralFormCreated || isGKVMedicalFormCreated,
    tour: PRODUCT_TOURS.dta,
  });

  if (isLoading(queryProgress)) {
    return (
      <>
        <Skeleton height={248} variant="rounded" width="100%" />
        <Skeleton height={90} variant="rounded" width="100%" />
      </>
    );
  }

  return (
    <>
      {missingIdentifiersMessage && (
        <Banner color="danger" message={missingIdentifiersMessage} />
      )}
      {/* Request Section */}
      <StyledFormCard className="p-4 shadow-[0px_0px_4px_0px_#00000014,0px_4px_14px_0px_#0000001F]">
        <div className="flex flex-col space-y-8">
          <div className="flex items-center space-x-2">
            <span>
              <FileOutputIcon size={20} />
            </span>
            <h2
              className="leading-5 font-bold"
              {...tourAttributes({
                stepKey: PRODUCT_TOURS.dta.steps.send_documents_heading.key,
                tourKey: PRODUCT_TOURS.dta.key,
              })}
            >
              {translations.insuranceApp.eligibilityTab.sendDocuments.title}
            </h2>
          </div>
          <div className="flex flex-col space-y-4">
            {ahbMessageThreads.length ? (
              <>
                <h3 className="text-xs leading-[16px] font-bold tracking-[0.08px] uppercase">
                  {
                    translations.insuranceApp.eligibilityTab.sendDocuments
                      .titleHistory
                  }
                </h3>
                <div className="w-full bg-beige-100 py-2 pl-6">
                  <ul className="w-full list-disc space-y-2 pl-6">
                    {ahbMessageThreads.map((ahbMessageThread) => {
                      const allMessages = [
                        ahbMessageThread.message,
                        ...(ahbMessageThread.chain ?? []),
                      ];

                      return allMessages.map((message) => (
                        <AHBRequestHistoryItem
                          key={getKey(message.message_id)}
                          message={message}
                        />
                      ));
                    })}
                  </ul>
                </div>
              </>
            ) : (
              <p className="text-sm">
                {
                  translations.insuranceApp.eligibilityTab.sendDocuments
                    .subtitle
                }
              </p>
            )}
            <h3 className="text-xs leading-[16px] font-bold tracking-[0.08px] uppercase">
              {
                translations.insuranceApp.eligibilityTab.sendDocuments
                  .titleDocuments
              }
            </h3>
            <div
              className="grid items-center gap-x-2 gap-y-4"
              style={{ gridTemplateColumns: "max-content auto" }}
            >
              {[
                {
                  formLabel: translations.patientForms.menu.rehabGeneralForm,
                  formName: REHAB_FORM_NAMES.GKV_GENERAL,
                  isGKVFormCreated: isGKVGeneralFormCreated,
                  testId: "go-to-general-form-pension",
                  tour: {
                    ...tourAttributes({
                      stepKey:
                        PRODUCT_TOURS.dta.steps.fill_in_gkv_general_form.key,
                      tourKey: PRODUCT_TOURS.dta.key,
                    }),
                  },
                },
                {
                  formLabel: translations.patientForms.menu.rehabMedicalForm,
                  formName: REHAB_FORM_NAMES.GKV_MEDICAL,
                  isApprovedByDoctor: isMedicalformApprovedByDoctor,
                  isGKVFormCreated: isGKVMedicalFormCreated,
                  testId: "go-to-medical-form-pension",
                  tour: {
                    ...tourAttributes({
                      stepKey:
                        PRODUCT_TOURS.dta.steps.fill_in_gkv_medical_form.key,
                      tourKey: PRODUCT_TOURS.dta.key,
                    }),
                  },
                },
              ].map(
                (
                  {
                    formLabel,
                    formName,
                    isApprovedByDoctor,
                    isGKVFormCreated,
                    testId,
                    tour,
                  },
                  i,
                ) => {
                  const isGKVMedicalFormRow =
                    formName === REHAB_FORM_NAMES.GKV_MEDICAL;
                  const { color, label } = getStatusChip({
                    isApprovedByDoctor,
                    isGKVFormCreated,
                    isGKVMedicalForm: isGKVMedicalFormRow,
                  });
                  return (
                    <Fragment key={getKey(testId, i)}>
                      <InfoChip
                        color={color}
                        disabled={!isGKVFormCreated}
                        label={label}
                      />
                      <div className="flex items-center space-x-2" {...tour}>
                        <p className="text-sm">{formLabel}</p>
                        <EligibilityAction
                          auction={auction}
                          formName={formName}
                          isApprovedByDoctor={!!isApprovedByDoctor}
                          isDRVFormCreated={isDRVFormCreated}
                          isGKVFormCreated={isGKVFormCreated}
                          isGKVMedicalForm={isGKVMedicalFormRow}
                          patient={patient}
                          testId={testId}
                        />
                      </div>
                    </Fragment>
                  );
                },
              )}
            </div>
          </div>
          {/* Attachment Section / File Section */}
          {activateDTAAttachments && (
            <>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-4">
                  <h3 className="text-xs leading-[16px] font-bold tracking-[0.08px] uppercase">
                    {
                      translations.insuranceApp.eligibilityTab.sendDocuments
                        .titleAttachments
                    }
                  </h3>
                  <UploadFilesModal
                    action={action as GeneralListAction<Filev2> | undefined}
                    availableFiles={availableFiles}
                    getFilesHISProgress={getFilesHISProgress}
                    handleSubmit={handleUploadFile}
                    handleUploadFilesHIS={handleUploadFilesHIS}
                    isDisabled={isFileUploadDisabled}
                    mutationProgress={mutationProgress}
                    reset={handleReset}
                    setAction={setAction}
                    unavailableFiles={unavailableFiles}
                    uploadButtonProps={{
                      children:
                        translations.insuranceApp.eligibilityTab.sendDocuments
                          .buttonAttachFiles,
                      id: "attach-files",
                      size: "sm",
                      startContent: <LinkIcon />,
                      tour: undefined,
                      variant: "light",
                    }}
                  />
                </div>
                <p className="text-sm">
                  {translations.insuranceApp.eligibilityTab.sendDocuments.informationAttachement(
                    {
                      totalSize: MAX_FILE_SIZE_AHB_MB.toFixed(1),
                    },
                  )}
                </p>
                {isFileSizeLimitExceeded && (
                  <Banner
                    color="warning"
                    message={translations.insuranceApp.eligibilityTab.attachmentSection.sizeLimitExceeded(
                      {
                        limit: MAX_FILE_SIZE_AHB_MB.toFixed(1),
                        totalSize: totalFileSizeMB.toFixed(1),
                      },
                    )}
                  />
                )}
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <p>
                    {
                      translations.insuranceApp.eligibilityTab.attachmentSection
                        .totalAttachments
                    }
                    {translations.general.colon}{" "}
                    {filteredUploadedFilesV2?.length || 0}
                  </p>
                  <p>
                    {
                      translations.insuranceApp.eligibilityTab.attachmentSection
                        .totalSize
                    }
                    {translations.general.colon} {totalFileSizeMB.toFixed(1)}MB
                    / {MAX_FILE_SIZE_AHB_MB.toFixed(1)}
                    MB
                  </p>
                </div>
                <ul className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 gap-y-2">
                  {filteredUploadedFilesV2?.map((file) => (
                    <li key={file.id} className="contents">
                      <div className="flex items-center space-x-2">
                        <LockIcon size={16} />
                        <p className="text-sm leading-5 font-bold">
                          {isFileHISStateful(file) ? (
                            file.document_file_name
                          ) : (
                            <AsyncFileName file={file} />
                          )}
                        </p>
                      </div>
                      <p className="text-right text-sm leading-5 font-medium text-gray-600">
                        {translations.patientForms.transitionalCareForm.savedDate(
                          {
                            date: formatUnixDate(
                              isFileHISStateful(file)
                                ? file.updated_at
                                : file.created_at,
                              locale,
                              { withTime: true },
                            ),
                          },
                        )}
                      </p>
                      <p className="text-right text-sm leading-5 font-medium text-gray-500">
                        ({getFileSize(file)}MB)
                      </p>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          color="primary"
                          id="view-attachment"
                          size="sm"
                          startContent={<ExternalLinkIcon />}
                          variant="light"
                          onPress={() => {
                            setAction({
                              data: file,
                              type: FILE_TABLE_ACTION_DOWNLOAD,
                            });
                          }}
                        >
                          {translations.actions.view}
                        </Button>
                        {!isFileDeleteDisabled && (
                          <Button
                            color="primary"
                            id="delete-attachment"
                            isLoading={isFileBeingDeleted({
                              action,
                              file,
                              mutationProgress,
                            })}
                            size="sm"
                            startContent={<TrashIcon />}
                            variant="light"
                            onPress={() => {
                              setAction({
                                data: file,
                                type: FILE_TABLE_ACTION_DELETE,
                              });
                            }}
                          >
                            {translations.actions.delete}
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              {canWithdrawMessage && mostRecentThread?.message && (
                <Button
                  color="danger"
                  id="withdraw-ahb-request"
                  isLoading={isLoading(withdrawProgress)}
                  size="sm"
                  style={{ margin: margin(0) }}
                  variant="solid"
                  onPress={() => {
                    handleWithdrawAHBRequest(mostRecentThread.message);
                  }}
                >
                  {
                    translations.insuranceApp.eligibilityTab.sendDocuments
                      .withdrawButton
                  }
                </Button>
              )}
              {shouldShowSendButton && (
                <Button
                  color="primary"
                  id="send-documents"
                  isDisabled={isSendButtonDisabled}
                  isLoading={
                    isAHBStatusRetrying(mostRecentThread?.message) ||
                    isLoading(sendProgress)
                  }
                  style={{ margin: margin(0) }}
                  tour={tourAttributes({
                    stepKey: PRODUCT_TOURS.dta.steps.send_documents_button.key,
                    tourKey: PRODUCT_TOURS.dta.key,
                  })}
                  variant="solid"
                  onPress={handleSendAHBRequest}
                >
                  {
                    translations.insuranceApp.eligibilityTab.sendDocuments
                      .submitFormButton
                  }
                </Button>
              )}
            </div>
            <div>
              {mostRecentThread?.message && !mostRecentThread.chain.length && (
                <RSButton
                  color="primary"
                  id="copy-xml"
                  loading={fetchXMLProgress}
                  size="small"
                  variant="text"
                  onClick={() =>
                    handleCopyXML(mostRecentThread.message.message_id)
                  }
                >
                  {
                    translations.insuranceApp.eligibilityTab.sendDocuments
                      .copyXmlButton
                  }
                </RSButton>
              )}
            </div>
          </div>
        </div>
      </StyledFormCard>
      {/* Response Section */}
      <StyledFormCard className="flex flex-col p-4 shadow-[0px_0px_4px_0px_#00000014,0px_4px_14px_0px_#0000001F]">
        <div
          className="flex flex-col space-y-4"
          {...tourAttributes({
            stepKey: PRODUCT_TOURS.dta.steps.reply_section.key,
            tourKey: PRODUCT_TOURS.dta.key,
          })}
        >
          <div className="flex items-center space-x-2">
            <span>
              <FileOutputIcon size={20} />
            </span>
            <h2 className="leading-5 font-bold">
              {
                translations.insuranceApp.eligibilityTab.replySection
                  .replySectionTitle
              }
            </h2>
          </div>
          {responses?.length ? (
            <ul className="flex list-none flex-col space-y-2">
              {responses.map((response) => (
                <li
                  key={response.message_id}
                  className="flex max-h-[24px] items-center gap-2 text-sm leading-6"
                >
                  <p className="inline-flex items-center gap-2">
                    {response.status !== AHB_RESPONSE_STATUS.SEEN && (
                      <span className="h-2 w-2 rounded-full bg-warning"></span>
                    )}
                    <span className="mr-1 font-semibold">
                      {response.status !== AHB_RESPONSE_STATUS.SEEN
                        ? translations.insuranceApp.eligibilityTab.replySection
                            .newResponse
                        : translations.insuranceApp.eligibilityTab.replySection
                            .openedResponse}
                    </span>
                    <span className="text-gray-600">
                      {isValid(response.received_at) &&
                        formatUnixDate(response.received_at, locale, {
                          withTime: true,
                        })}
                    </span>
                  </p>
                  <RSButton
                    color="primary"
                    id={`view-document-${response.message_id}`}
                    loading={progress(response.response_id)}
                    size="small"
                    style={{ alignItems: "center", display: "flex" }}
                    trackEventProps={{
                      name: TRACK_EVENTS.REHAB_FORM_VIEW_DOCUMENT,
                    }}
                    variant="text"
                    onClick={() => {
                      handleViewDocument(response.response_id);
                    }}
                  >
                    <span className="pr-2">
                      <ExternalLinkIcon size={16} />
                    </span>
                    {
                      translations.insuranceApp.eligibilityTab.replySection
                        .viewDocument
                    }
                  </RSButton>
                  <RSButton
                    color="primary"
                    id={`print-document-${response.message_id}`}
                    loading={progress2(response.response_id)}
                    size="small"
                    style={{ alignItems: "center", display: "flex" }}
                    trackEventProps={{
                      name: TRACK_EVENTS.REHAB_FORM_PRINT_DOCUMENT,
                    }}
                    variant="text"
                    onClick={() => {
                      handlePrintDocument(response.response_id);
                    }}
                  >
                    <span className="pr-2">
                      <PrinterIcon size={16} />
                    </span>
                    {
                      translations.insuranceApp.eligibilityTab.replySection
                        .printDocument
                    }
                  </RSButton>
                  {hasTwoWaySync && (
                    <RSButton
                      color="primary"
                      id={`send-ahb-to-his-${response.message_id}`}
                      loading={sendToHisProgress(response.response_id)}
                      size="small"
                      style={{ alignItems: "center", display: "flex" }}
                      trackEventProps={{
                        name: TRACK_EVENTS.REHAB_SEND_AHB_TO_HIS,
                      }}
                      variant="text"
                      onClick={() => {
                        handleSendToHis(response.response_id);
                      }}
                    >
                      <span className="pr-2">
                        <LucideFolderInput size={16} />
                      </span>
                      {
                        translations.insuranceApp.eligibilityTab.replySection
                          .sentToHisButton
                      }
                    </RSButton>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col">
              <p className="text-sm text-gray-600">
                {
                  translations.insuranceApp.eligibilityTab.replySection
                    .noReplyInfo
                }
              </p>
            </div>
          )}
        </div>
      </StyledFormCard>
      {confirmSendRequestProps.opened && (
        <ConfirmationDialog {...confirmSendRequestProps} />
      )}
      {withdrawDialogProps.opened && (
        <WithdrawMessageReasonModal {...withdrawDialogProps} />
      )}
      {activateFileDeleteModalRedesign ? (
        <FileDeleteModalv2
          file={action?.data as FileState}
          handleDelete={handleDeleteFile}
          isOpen={action?.type === FILE_TABLE_ACTION_DELETE}
          mutationProgress={mutationProgress}
          reset={handleReset}
        />
      ) : (
        <FileDeleteModal
          file={action?.data as FileState}
          handleDelete={handleDeleteFile}
          isOpen={action?.type === FILE_TABLE_ACTION_DELETE}
          mutationProgress={mutationProgress}
          reset={handleReset}
        />
      )}
    </>
  );
}
